import { formatPercentage } from '@fortress-technology-solutions/fortress-component-library/utils';
import * as formatter from '../../utils/formatter';

/** Fixed columns for the Floor Plan Summary CSV block (not tied to table column picker). */
export const FLOOR_PLAN_SUMMARY_CSV_HEADERS = [
  { id: 'floorPlan', label: 'Floor Plan' },
  { id: 'units', label: 'Units' },
  { id: 'sqFt', label: 'SqFt' },
  { id: 'marketRent', label: 'Market Rent' },
  { id: 'quotingRent', label: 'Quoting Rent' },
  { id: 'leasedRentAverage', label: 'Leased Rent Average' },
  { id: 'marketRentSqFt', label: 'Market Rent/SqFt' },
  { id: 'leasedRentSqFt', label: 'Leased Rent/SqFt' },
  { id: 'occupiedUnits', label: 'Occupied Units' },
  { id: 'occupancyPct', label: 'Occupancy %' },
];

export const buildFloorPlanSummaryForCSVExport = ({ results }) => {
  const floorPlans = {};
  results.forEach((unit) => {
    const floorPlanValues = floorPlans[unit.floorPlan] || {
      leasedRentTotal: 0,
      leasedUnits: 0,
      leasedSqFt: 0,
      marketRentSqFt: 0,
      marketRentTotal: 0,
      marketRentUnits: 0,
      occupiedUnits: 0,
      quotingRent: 0,
      sqFt: 0,
      requestedHardshipExemption: '',
      units: 0,
    };

    floorPlanValues.sqFt = unit.sqFt || 0;

    const currentLeasedRentValue = unit.leasedRent || 0;
    if (currentLeasedRentValue) {
      floorPlanValues.leasedRentTotal += currentLeasedRentValue;
      floorPlanValues.leasedUnits += 1;
      floorPlanValues.leasedSqFt += unit.sqFt || 0;
    }

    const floorPlanMarketRent = unit.marketRent;
    if (floorPlanMarketRent) {
      floorPlanValues.marketRentTotal += floorPlanMarketRent;
      floorPlanValues.marketRentUnits += 1;
      floorPlanValues.marketRentSqFt += unit.sqFt || 0;
    }

    floorPlanValues.units += 1;
    floorPlanValues.occupiedUnits += !unit.isVacant ? 1 : 0;

    // Accumulate quoting rent - use the calculated quotingRent from the unit
    const unitQuotingRent = unit.quotingRent ?? 0;
    floorPlanValues.quotingRent += unitQuotingRent;

    floorPlans[unit.floorPlan] = floorPlanValues;
  });

  let fpLeasedRentAverageTotal = 0;
  let fpLeasedUnits = 0;
  let fpLeasedSqFt = 0;
  let fpMarketRentAverageTotal = 0;
  let fpMarketRentSqFt = 0;
  let fpMarketRentUnits = 0;
  let fpOccupiedUnits = 0;
  let quotingRentTotal = 0;
  let fpSqFtTotal = 0;
  let fpUnits = 0;

  const floorPlanRows = Object.entries(floorPlans).map(([key, fp]) => {
    const {
      leasedRentTotal,
      leasedUnits,
      leasedSqFt,
      marketRentSqFt,
      marketRentTotal,
      marketRentUnits,
      occupiedUnits,
      quotingRent,
      sqFt,
      units: numUnits,
    } = fp;

    const leasedRentAverage =
      leasedUnits > 0 ? leasedRentTotal / leasedUnits : 0;
    const leasedRentSqFt =
      leasedUnits > 0 && leasedSqFt > 0
        ? leasedRentAverage / (leasedSqFt / leasedUnits)
        : 0;
    const marketRentAverage =
      marketRentUnits > 0 ? marketRentTotal / marketRentUnits : 0;
    const marketRentSqFeet =
      marketRentAverage > 0 && marketRentSqFt > 0 && marketRentUnits > 0
        ? marketRentAverage / (marketRentSqFt / marketRentUnits)
        : 0;
    const occupancyPct = numUnits > 0 ? occupiedUnits / numUnits : 0;

    fpLeasedRentAverageTotal += leasedRentTotal;
    fpLeasedSqFt += leasedSqFt;
    fpLeasedUnits += leasedUnits;
    fpMarketRentAverageTotal += marketRentTotal;
    fpMarketRentSqFt += marketRentSqFt;
    fpMarketRentUnits += marketRentUnits;
    fpOccupiedUnits += occupiedUnits;
    quotingRentTotal += quotingRent;
    fpSqFtTotal += (sqFt || 0) * numUnits;
    fpUnits += numUnits;

    const quotingRentAverage = numUnits > 0 ? quotingRent / numUnits : 0;

    return {
      floorPlan: { value: key, variant: 'string' },
      units: { value: numUnits ?? 0, variant: 'number' },
      sqFt: { value: sqFt ?? 0, variant: 'number' },
      marketRent: {
        value: marketRentAverage,
        variant: 'currency',
      },
      quotingRent: { value: quotingRentAverage, variant: 'currency' },
      leasedRentAverage: { value: leasedRentAverage, variant: 'currency' },
      marketRentSqFt: {
        value: formatter.round(Number(marketRentSqFeet ?? 0)),
        variant: 'currency',
      },
      leasedRentSqFt: {
        value: formatter.round(Number(leasedRentSqFt ?? 0), 3),
        variant: 'currency',
      },
      occupiedUnits: { value: occupiedUnits ?? 0, variant: 'number' },
      occupancyPct: {
        value: formatPercentage(occupancyPct),
        variant: 'string',
      },
    };
  });

  const fpSqFtGrand = fpUnits > 0 ? Math.round(fpSqFtTotal / fpUnits) : 0;
  const fpLeasedRentAverageGrand =
    fpLeasedUnits > 0 ? fpLeasedRentAverageTotal / fpLeasedUnits : 0;
  const fpLeasedRentSqFtGrand =
    fpLeasedUnits > 0 && fpLeasedSqFt > 0
      ? fpLeasedRentAverageGrand / (fpLeasedSqFt / fpLeasedUnits)
      : 0;
  const fpMarketRentGrand =
    fpMarketRentUnits > 0 ? fpMarketRentAverageTotal / fpMarketRentUnits : 0;
  const fpMarketRentSqFtGrand =
    fpMarketRentUnits > 0 && fpMarketRentSqFt > 0
      ? fpMarketRentGrand / (fpMarketRentSqFt / fpMarketRentUnits)
      : 0;
  const fpOccupancyPctGrand = fpUnits > 0 ? fpOccupiedUnits / fpUnits : 0;
  const quotingRentGrand = fpUnits > 0 ? quotingRentTotal / fpUnits : 0;

  floorPlanRows.push({
    floorPlan: { value: 'Grand Total', variant: 'string' },
    units: { value: fpUnits, variant: 'number' },
    sqFt: { value: fpSqFtGrand, variant: 'number' },
    marketRent: {
      value: formatter.round(fpMarketRentGrand),
      variant: 'currency',
    },
    quotingRent: {
      value: formatter.round(quotingRentGrand),
      variant: 'currency',
    },
    leasedRentAverage: {
      value: fpLeasedRentAverageGrand,
      variant: 'currency',
    },
    marketRentSqFt: {
      value: formatter.round(fpMarketRentSqFtGrand),
      variant: 'currency',
    },
    leasedRentSqFt: {
      value: formatter.round(fpLeasedRentSqFtGrand),
      variant: 'currency',
    },
    occupiedUnits: { value: fpOccupiedUnits ?? 0, variant: 'number' },
    occupancyPct: {
      value: formatPercentage(fpOccupancyPctGrand),
      variant: 'string',
    },
  });

  return floorPlanRows;
};

/**
 * Custom filter function for lead sources that handles comma-separated values
 * @param {string[]} filterByArr - Array of selected lead source names from filter (already lowercase)
 * @param {string} filterValue - Comma-separated string of all lead sources for a unit
 * @returns {boolean} True if any selected lead source is found in the unit's lead sources
 */
export const leadSourceFilterFn = (filterByArr, filterValue) => {
  if (!filterByArr || filterByArr.length === 0) {
    return true; // No filter applied, show all
  }

  if (!filterValue || filterValue === 'N/A') {
    return false; // No lead sources to match against
  }

  // Split the comma-separated string into individual lead sources and convert to lowercase
  const unitLeadSources = filterValue
    .split(',')
    .map((source) => source.trim().toLowerCase())
    .filter((source) => source.length > 0);

  // Check if any selected lead source matches any of the unit's lead sources
  // Note: filterByArr is already lowercase from the table component
  return filterByArr.some((selected) => unitLeadSources.includes(selected));
};

const getHeaderLabel = (label) =>
  typeof label === 'string' ? label : label?.props?.defaultMessage ?? '';

export const buildCSVFooterRows = ({
  footerRow,
  summaryRows,
  summaryHeaders = FLOOR_PLAN_SUMMARY_CSV_HEADERS,
}) => {
  const rentRollFooterRows = [];
  const summaryFooterRows = [];

  let totalsRow = undefined;
  let summaryBlock = undefined;

  // Totals row (respects rent roll column selection)
  if (footerRow && Object.keys(footerRow).length > 0) {
    totalsRow = {
      ...footerRow,
      unit: { value: 'Grand Total', variant: 'string' },
      quotingRent: footerRow.quotingRent ?? { value: '—', variant: 'string' },
      occupancyPct: footerRow.occupancyPct ?? { value: '—', variant: 'string' },
    };

    rentRollFooterRows.push({}, totalsRow, {});
  }

  // Floor Plan Summary block (always uses fixed summary columns)
  if (Array.isArray(summaryRows) && summaryRows.length > 0) {
    summaryBlock = summaryRows;

    const summaryHeaderIds = summaryHeaders.map((h) => h.id);
    const firstSummaryColumnId = summaryHeaderIds[0];

    const titleRow = {};
    summaryHeaderIds.forEach((id) => {
      titleRow[id] = {
        value: id === firstSummaryColumnId ? 'Floor Plan Summary' : '',
        variant: 'string',
      };
    });

    const labelRow = {};
    summaryHeaders.forEach(({ id, label }) => {
      labelRow[id] = {
        value: getHeaderLabel(label),
        variant: 'string',
      };
    });

    summaryFooterRows.push(titleRow, labelRow, ...summaryRows);
  }

  return {
    fullRows: [...rentRollFooterRows, ...summaryFooterRows],
    rentRollFooterRows,
    summaryFooterRows,
    totalsRow,
    summaryRows: summaryBlock,
  };
};
